import FormControlLabel from '@mui/material/FormControlLabel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import ListViewTable from 'components/Common/ListViewTable';
import PageHeader from 'components/Common/PageHeader';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  BillableSourceType,
  useBulkDeleteBillableConfig,
  useUpsertBillableConfig,
} from 'api/billableConfigs';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CleanRoomTypeCell } from 'components/CleanRooms/CrListLayout/componentsCell';
import { HSpinner } from 'BaseComponents';
import { axiosInstance } from 'utils/http';
import { canSeeBillableFeatures } from 'utils/permissionUtils';
import { cleanRoom, flow } from 'utils/urlConstants';
import { crumbs } from 'components/Common/BreadcrumbsNavigation';
import { showAlert } from 'redux/actions/AlertActions';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchCleanRoomPartners } from 'hooks/cleanRoomAPIs';
import { useFetchCleanRoomQuestionList } from 'hooks/questionAPIs';
import { useMutation, useQuery, useQueryClient } from 'react-query';


const sortByName = (items, nameField = 'name') => {
  if (!items || !Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    const nameA = (nameField === 'name' ? (a.title || a.name) : a[nameField]) || '';
    const nameB = (nameField === 'name' ? (b.title || b.name) : b[nameField]) || '';
    return nameA.localeCompare(nameB);
  });
};

// Billable Switch Component for Clean Room level
const BillableSwitch = ({
  billable,
  onToggle,
  isLoading,
  disabled,
}) => (
  <FormControlLabel
    control={(
      <Switch
        checked={billable}
        onChange={(e) => onToggle(e.target.checked)}
        color='primary'
        disabled={disabled || isLoading}
      />
    )}
    label={isLoading ? <HSpinner isButton /> : ''}
  />
);

// Question Row Component
const QuestionRow = ({
  question, cleanRoomBillableConfig, refetchQuestions,
}) => {
  const [questionBillable, setQuestionBillable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    mutate: upsertCrqBillableConfig,
  } = useUpsertBillableConfig();

  // Initialize billable state from question data
  useEffect(() => {
    if (question.isBillable !== undefined && question.isBillable !== null) {
      // Use isBillable from API response
      setQuestionBillable(question.isBillable);
    }
    else if (cleanRoomBillableConfig) {
      // Fallback to cleanroom billable config if question doesn't have value
      setQuestionBillable(cleanRoomBillableConfig.value === 'true');
    }
    else {
      // Default to false if neither exists
      setQuestionBillable(false);
    }
  }, [question.isBillable, cleanRoomBillableConfig]);

  const handleToggle = (checked) => {
    setIsUpdating(true);
    setQuestionBillable(checked);

    const payload = {
      sourceID: question.ID,
      sourceType: BillableSourceType.BILLABLE_CRQ,
      value: checked.toString(),
      ...(question.billableConfigID && { ID: question.billableConfigID }),
    };

    upsertCrqBillableConfig(payload, {
      onSuccess: () => {
        setIsUpdating(false);
        // Refetch questions to get updated billableConfigID
        refetchQuestions({});
      },
      onError: () => {
        setIsUpdating(false);
        setQuestionBillable(!checked);
      },
    });
  };

  return (
    <TableRow>
      <TableCell>{question.title || question.name}</TableCell>
      <TableCell>{question.displayID}</TableCell>
      <TableCell align='right'>
        <Box display='flex' justifyContent='flex-end'>
          <BillableSwitch
            billable={questionBillable}
            onToggle={handleToggle}
            isLoading={isUpdating}
            disabled={isUpdating}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
};

// Partner/Owner Row Component
const StakeholderRow = ({
  stakeholder, crId, queryClient,
}) => {
  const [stakeholderBillable, setStakeholderBillable] = useState(true); // Default to true
  const [isUpdating, setIsUpdating] = useState(false);

  const isOwner = stakeholder?.isOwner;

  // Determine source ID and type for BillableConfig API
  const sourceId = isOwner ? crId : stakeholder?.partnerID;
  const sourceType = isOwner
    ? BillableSourceType.BILLABLE_OWNER
    : BillableSourceType.BILLABLE_PARTNER;

  // Upsert billable config mutation
  const { mutate: upsertBillableConfig } = useUpsertBillableConfig();

  // Initialize billable state from stakeholder data
  useEffect(() => {
    if (stakeholder.isBillable !== undefined && stakeholder.isBillable !== null) {
      // Use isBillable from API response
      setStakeholderBillable(stakeholder.isBillable);
    }
    else {
      // Default to true if not present
      setStakeholderBillable(true);
    }
  }, [stakeholder.isBillable]);

  const handleToggle = (checked) => {
    setIsUpdating(true);
    setStakeholderBillable(checked);

    upsertBillableConfig({
      sourceID: sourceId,
      sourceType,
      value: checked.toString(),
      ...(stakeholder.billableConfigID && { ID: stakeholder.billableConfigID }),
    }, {
      onSuccess: (response) => {
        setIsUpdating(false);
        // Update cache with new isBillable value and configID
        queryClient.setQueryData(['Partners'], (oldResponse) => {
          // Partners data might be stored as response.data.cleanRoomOrg
          if (!oldResponse?.data?.cleanRoomOrg) return oldResponse;
          return {
            ...oldResponse,
            data: {
              ...oldResponse.data,
              cleanRoomOrg: oldResponse.data.cleanRoomOrg.map(p => {
                if (p.organizationID === stakeholder.organizationID) {
                  return {
                    ...p,
                    isBillable: checked,
                    billableConfigID: response?.billableConfigID || '',
                  };
                }
                return p;
              }),
            },
          };
        });
      },
      onError: () => {
        setIsUpdating(false);
        // Revert the UI state on error
        setStakeholderBillable(!checked);
      },
    });
  };

  return (
    <TableRow>
      <TableCell>{stakeholder?.name}</TableCell>
      <TableCell>{stakeholder?.organizationID}</TableCell>
      <TableCell>{isOwner ? 'Owner' : 'Partner'}</TableCell>
      <TableCell align='right'>
        <Box display='flex' justifyContent='flex-end'>
          <BillableSwitch
            billable={stakeholderBillable}
            onToggle={handleToggle}
            isLoading={isUpdating}
            disabled={isUpdating}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
};

// Flow Row Component
const FlowRow = ({
  flowItem, cleanRoomBillableConfig, queryClient, orgId, crId,
}) => {
  const [flowBillable, setFlowBillable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    mutate: upsertFlowBillableConfig,
  } = useUpsertBillableConfig();

  // Initialize billable state from flow data
  useEffect(() => {
    if (flowItem.isBillable !== undefined && flowItem.isBillable !== null) {
      // Use isBillable from API response
      setFlowBillable(flowItem.isBillable);
    }
    else if (cleanRoomBillableConfig) {
      // Fallback to cleanroom billable config if flow doesn't have value
      setFlowBillable(cleanRoomBillableConfig.value === 'true');
    }
    else {
      // Default to false if neither exists
      setFlowBillable(false);
    }
  }, [flowItem.isBillable, cleanRoomBillableConfig]);

  const handleToggle = (checked) => {
    setIsUpdating(true);
    setFlowBillable(checked);

    const payload = {
      sourceID: flowItem.ID,
      sourceType: BillableSourceType.BILLABLE_FLOW,
      value: checked.toString(),
      ...(flowItem.billableConfigID && { ID: flowItem.billableConfigID }),
    };

    upsertFlowBillableConfig(payload, {
      onSuccess: (response) => {
        setIsUpdating(false);

        // Update cache with new isBillable value and configID from API
        queryClient.setQueryData(['CleanRoomFlows', orgId, crId], (oldResponse) => {
          if (!oldResponse?.data?.cleanRoomFlows) return oldResponse;

          const updatedFlows = oldResponse.data.cleanRoomFlows.map(f => {
            if (f.ID === flowItem.ID) {
              const newConfigID = response?.billableConfigID || '';
              return {
                ...f,
                isBillable: checked,
                billableConfigID: newConfigID,
              };
            }
            return f;
          });

          return {
            ...oldResponse,
            data: {
              ...oldResponse.data,
              cleanRoomFlows: updatedFlows,
            },
          };
        });
      },
      onError: () => {
        setIsUpdating(false);
        // Revert the UI state on error
        setFlowBillable(!checked);
      },
    });
  };

  return (
    <TableRow>
      <TableCell>{flowItem.flow?.name || flowItem.name}</TableCell>
      <TableCell>{flowItem.flow?.displayID || flowItem.displayID || flowItem.ID}</TableCell>
      <TableCell align='right'>
        <Box display='flex' justifyContent='flex-end'>
          <BillableSwitch
            billable={flowBillable}
            onToggle={handleToggle}
            isLoading={isUpdating}
            disabled={isUpdating}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
};

// Main Row Component for Clean Rooms
function Row({
  row, open, handleOnClick, orgId, expandedState, setExpandedState,
}) {
  const queryClient = useQueryClient();
  const [crBillable, setCrBillable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { questionsOpen, partnersOpen, flowsOpen } = expandedState;
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [pendingBillableValue, setPendingBillableValue] = useState(false);
  const [questionsWithBillableConfig, setQuestionsWithBillableConfig] = useState([]);
  const [flowsWithBillableConfig, setFlowsWithBillableConfig] = useState([]);

  const canSeeBillableToggle = canSeeBillableFeatures(row.type);

  // Use cleanroom billable config from row data
  const cleanRoomBillableConfig = useMemo(() => {
    if (row.isBillable !== undefined && row.isBillable !== null) {
      return { value: row.isBillable.toString(), ID: row.billableConfigID };
    }
    return null;
  }, [row.isBillable, row.billableConfigID]);

  const {
    mutate: upsertCrBillableConfig,
  } = useUpsertBillableConfig();

  const {
    mutate: bulkDeleteBillableConfig,
  } = useBulkDeleteBillableConfig();

  // Fetch questions for this clean room
  const {
    data: questionsData,
    mutate: fetchQuestions,
    isLoading: isLoadingQuestions,
  } = useFetchCleanRoomQuestionList(row.ID);

  const fetchQuestionsRef = useRef(fetchQuestions);
  useEffect(() => {
    fetchQuestionsRef.current = fetchQuestions;
  }, [fetchQuestions]);

  // Sort questions alphabetically by title/name
  const sortedQuestions = useMemo(() => sortByName(questionsData?.questions),
    [questionsData?.questions],
  );

  // Fetch partners for this clean room when row is opened
  const {
    data: partnersData,
    isLoading: isLoadingStakeholders,
  } = useFetchCleanRoomPartners(orgId, open ? row.ID : null);

  const {
    data: flowsData,
    isLoading: isLoadingFlows,
    refetch: refetchFlows,
  } = useQuery(
    ['CleanRoomFlows', orgId, row.ID],
    async () => axiosInstance.get(flow(orgId).list(row.ID)),
    {
      enabled: open && !!row.ID, // Only fetch when panel is opened
      select: (res) => res.data?.cleanRoomFlows || [],
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  // Create stakeholders list including owner + partners
  const stakeholdersData = useMemo(() => {
    const stakeholders = [];

    // Add all partners (which includes both owner and partner organizations)
    if (partnersData && Array.isArray(partnersData)) {
      partnersData.forEach(partner => {
        stakeholders.push({
          organizationID: partner.organizationID,
          partnerID: partner.partnerID,
          name: partner.name || `Organization ${partner.organizationID}`,
          isOwner: partner.organizationID === orgId,
          isBillable: partner.isBillable,
          billableConfigID: partner.billableConfigID,
        });
      });
    }

    // Sort stakeholders alphabetically by name
    return sortByName(stakeholders);
  }, [orgId, partnersData]);

  // Sort flows alphabetically by name
  const sortedFlows = useMemo(() => sortByName(flowsData),
    [flowsData],
  );


  // Function to check if any questions have custom BILLABLE config
  const checkQuestionsConfig = useCallback(() => {
    if (!sortedQuestions || sortedQuestions.length === 0) return [];

    // Filter questions that have custom billable config (billableConfigID is present)
    return sortedQuestions
      .filter(question => question.billableConfigID)
      .map(question => ({
        question,
        config: { ID: question.billableConfigID },
      }));
  }, [sortedQuestions]);

  // Function to check if any flows have custom BILLABLE config
  const checkFlowsConfig = useCallback(() => {
    if (!sortedFlows || sortedFlows.length === 0) return [];

    // Filter flows that have custom billable config (billableConfigID is present)
    return sortedFlows
      .filter(flowItem => flowItem.billableConfigID)
      .map(flowItem => ({
        flowItem,
        config: { ID: flowItem.billableConfigID },
      }));
  }, [sortedFlows]);


  // Set up billable state from clean room billable config
  useEffect(() => {
    if (cleanRoomBillableConfig) {
      setCrBillable(cleanRoomBillableConfig.value === 'true');
    }
    else {
      setCrBillable(false); // Default to false if no config exists
    }
  }, [cleanRoomBillableConfig]);

  // Fetch questions when clean room panel is opened
  useEffect(() => {
    if (open && row.ID) {
      fetchQuestionsRef.current({});
    }
  }, [open, row.ID]);

  const updateCleanRoomBillable = (checked) => {
    setIsUpdating(true);
    setCrBillable(checked);

    const payload = {
      sourceID: row.ID,
      sourceType: BillableSourceType.BILLABLE_CR,
      value: checked.toString(),
    };

    // Include ID if updating existing config
    if (row.billableConfigID) {
      payload.ID = row.billableConfigID;
    }

    upsertCrBillableConfig(payload, {
      onSuccess: () => {
        // Refetch questions & flows to show updated inherited values
        setCrBillable(checked);
        fetchQuestionsRef.current({});
        refetchFlows();
        setIsUpdating(false);
      },
      onError: () => {
        setIsUpdating(false);
        // Revert the UI state on error
        setCrBillable(!checked);
      },
    });
  };

  const handleCrBillableToggle = (checked) => {
    setPendingBillableValue(checked);

    // Check for custom configs in questions and flows
    const questionsWithConfigData = checkQuestionsConfig();
    const flowsWithConfigData = checkFlowsConfig();

    // Show dialog when CR billable is changed and there are specific configs
    if (questionsWithConfigData.length > 0 || flowsWithConfigData.length > 0) {
      // Store the questions and flows with config for later deletion
      setQuestionsWithBillableConfig(questionsWithConfigData);
      setFlowsWithBillableConfig(flowsWithConfigData);
      setShowOverrideDialog(true);
      return;
    }

    // No custom configs found, proceed with update
    updateCleanRoomBillable(checked);
  };

  const handleOverrideConfirm = () => {
    setShowOverrideDialog(false);
    setIsUpdating(true);

    // Step 1: Collect all config IDs to delete
    const configIDsToDelete = [];

    questionsWithBillableConfig.forEach(({ config }) => {
      if (config?.ID) {
        configIDsToDelete.push(config.ID);
      }
    });

    flowsWithBillableConfig.forEach(({ config }) => {
      if (config?.ID) {
        configIDsToDelete.push(config.ID);
      }
    });

    // Prepare cleanroom config payload
    const payload = {
      sourceID: row.ID,
      sourceType: BillableSourceType.BILLABLE_CR,
      value: pendingBillableValue.toString(),
      ...(row.billableConfigID && { ID: row.billableConfigID }),
    };

    // Step 2: Delete custom configs first (if any), then update cleanroom config
    if (configIDsToDelete.length > 0) {
      bulkDeleteBillableConfig(configIDsToDelete, {
        onSuccess: () => {
          // After successful delete, update cleanroom config
          upsertCrBillableConfig(payload, {
            onSuccess: () => {
              // Refetch only questions & flows for this CR
              setCrBillable(pendingBillableValue);
              fetchQuestionsRef.current({});
              refetchFlows();
              setQuestionsWithBillableConfig([]);
              setFlowsWithBillableConfig([]);
              setIsUpdating(false);
            },
            onError: () => {
              setIsUpdating(false);
              setCrBillable(!pendingBillableValue);
            },
          });
        },
        onError: () => {
          setIsUpdating(false);
          setCrBillable(!pendingBillableValue);
        },
      });
    }
    else {
      // No configs to delete, just update cleanroom config
      upsertCrBillableConfig(payload, {
        onSuccess: () => {
          setCrBillable(pendingBillableValue);
          // Refetch only questions & flows for this CR
          fetchQuestionsRef.current({});
          refetchFlows();
          setQuestionsWithBillableConfig([]);
          setFlowsWithBillableConfig([]);
          setIsUpdating(false);
        },
        onError: () => {
          setIsUpdating(false);
          setCrBillable(!pendingBillableValue);
        },
      });
    }
  };

  const handleOverrideCancel = () => {
    setShowOverrideDialog(false);
    setIsUpdating(true);

    const payload = {
      sourceID: row.ID,
      sourceType: BillableSourceType.BILLABLE_CR,
      value: pendingBillableValue.toString(),
      ...(row.billableConfigID && { ID: row.billableConfigID }),
    };

    // Update CR but keep custom configs
    upsertCrBillableConfig(payload, {
      onSuccess: () => {
        setCrBillable(pendingBillableValue);
        // Refetch to show inherited values
        fetchQuestionsRef.current({});
        refetchFlows();
        setQuestionsWithBillableConfig([]);
        setFlowsWithBillableConfig([]);
        setIsUpdating(false);
      },
      onError: () => {
        setIsUpdating(false);
        setCrBillable(!pendingBillableValue);
      },
    });
  };

  const handleDialogClose = () => {
    // User clicked X or outside - cancel everything
    setShowOverrideDialog(false);
    setQuestionsWithBillableConfig([]);
    setFlowsWithBillableConfig([]);
    // Revert toggle to original state
    setCrBillable(!pendingBillableValue);
  };

  // Hide row for unsupported clean room types
  if (!canSeeBillableToggle) {
    return null;
  }

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label='expand row'
            size='small'
            onClick={handleOnClick}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component='th' scope='row'>
          {row.name}
        </TableCell>
        <TableCell align='right'>{row.displayID}</TableCell>
        <TableCell align='center'>
          <Box display='flex' justifyContent='center'>
            <CleanRoomTypeCell row={row} />
          </Box>
        </TableCell>
        <TableCell align='right'>
          <Box display='flex' justifyContent='flex-end'>
            <BillableSwitch
              billable={crBillable}
              onToggle={handleCrBillableToggle}
              isLoading={isUpdating}
              disabled={isUpdating}
            />
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ margin: 1, width: '100%' }}>
              {/* Questions Section */}
              <Box mb={2}>
                <Button
                  onClick={() => setExpandedState({ questionsOpen: !questionsOpen })}
                  startIcon={questionsOpen ? <KeyboardArrowUp /> : <KeyboardArrowDownIcon />}
                  sx={{ mb: 1 }}
                >
                  Questions (
                  {sortedQuestions?.length || 0}
                  )
                </Button>
                <Collapse in={questionsOpen} timeout='auto' unmountOnExit>
                  <ListViewTable list={!isLoadingQuestions || !!questionsData} skeletonHeight={200}>
                    <Table size='small' aria-label='questions'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>ID</TableCell>
                          <TableCell align='right'>Billable</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedQuestions?.map((question) => (
                          <QuestionRow
                            key={question.ID}
                            question={question}
                            cleanRoomBillableConfig={cleanRoomBillableConfig}
                            refetchQuestions={fetchQuestions}
                          />
                        ))}
                        {(!sortedQuestions || sortedQuestions.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={3} align='center'>
                              <Typography variant='body2' color='textSecondary'>
                                No questions found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ListViewTable>
                </Collapse>
              </Box>

              {/* Partners Section */}
              <Box>
                <Button
                  onClick={() => setExpandedState({ partnersOpen: !partnersOpen })}
                  startIcon={partnersOpen ? <KeyboardArrowUp /> : <KeyboardArrowDownIcon />}
                  sx={{ mb: 1 }}
                >
                  Partners & Owners (
                  {stakeholdersData?.length || 0}
                  )
                </Button>
                <Collapse in={partnersOpen} timeout='auto' unmountOnExit>
                  <ListViewTable
                    list={!isLoadingStakeholders || !!partnersData}
                    skeletonHeight={200}
                  >
                    <Table size='small' aria-label='partners'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Organization ID</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align='right'>Billable</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stakeholdersData?.map((stakeholder) => (
                          <StakeholderRow
                            key={`${row.ID}-${stakeholder.organizationID}-${stakeholder.isOwner ? 'owner' : 'partner'}`}
                            stakeholder={stakeholder}
                            crId={row.ID}
                            queryClient={queryClient}
                          />
                        ))}
                        {(!stakeholdersData || stakeholdersData.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={4} align='center'>
                              <Typography variant='body2' color='textSecondary'>
                                No partners found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ListViewTable>
                </Collapse>
              </Box>

              {/* Flows Section */}
              <Box>
                <Button
                  onClick={() => setExpandedState({ flowsOpen: !flowsOpen })}
                  startIcon={flowsOpen ? <KeyboardArrowUp /> : <KeyboardArrowDownIcon />}
                  sx={{ mb: 1 }}
                >
                  Flows (
                  {sortedFlows?.length || 0}
                  )
                </Button>
                <Collapse in={flowsOpen} timeout='auto' unmountOnExit>
                  <ListViewTable list={!isLoadingFlows || !!flowsData} skeletonHeight={200}>
                    <Table size='small' aria-label='flows'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>ID</TableCell>
                          <TableCell align='right'>Billable</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedFlows?.map((flowItem) => (
                          <FlowRow
                            key={flowItem.ID}
                            flowItem={flowItem}
                            cleanRoomBillableConfig={cleanRoomBillableConfig}
                            queryClient={queryClient}
                            orgId={orgId}
                            crId={row.ID}
                          />
                        ))}
                        {(!sortedFlows || sortedFlows.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={3} align='center'>
                              <Typography variant='body2' color='textSecondary'>
                                No flows found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ListViewTable>
                </Collapse>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Override Confirmation Dialog */}
      <Dialog
        open={showOverrideDialog}
        onClose={handleDialogClose}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Override Billable Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            {(() => {
              if (questionsWithBillableConfig.length > 0 && flowsWithBillableConfig.length > 0) {
                return 'Questions and Flows in this clean room have specific Billable configuration.';
              }
              if (questionsWithBillableConfig.length > 0) {
                return 'Questions in this clean room have specific Billable configuration.';
              }
              return 'Flows in this clean room have specific Billable configuration.';
            })()}
            {' '}
            Do you wish to override this config with the new Clean Room Billable setting?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverrideCancel} color='inherit'>
            No
          </Button>
          <Button
            onClick={handleOverrideConfirm}
            variant='contained'
            color='primary'
            disabled={isUpdating}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const ManageBilling = () => {
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);
  const [openRowId, setOpenRowId] = useState(null);
  const [expandedStates, setExpandedStates] = useState({}); // Track expanded states by row ID

  const dataURL = cleanRoom(orgId).list;

  const dispatch = useDispatch();
  const [dataList, setDataList] = useState({});

  const {
    isLoading: isListLoading,
    mutate: mutateList,
    isFetching: isListFetching,
  } = useMutation(
    'manage-billing-get-list-data',
    async () => axiosInstance.post(dataURL, {}),
    {
      onSuccess: (res) => setDataList(res?.data ?? {}),
      refetchOnWindowFocus: false,
      onError: (err) => dispatch(showAlert({
        message: err.response?.data?.message || 'Error loading data',
        type: 'error',
      })),
    },
  );

  // Sort clean rooms alphabetically by name
  const sortedCleanRooms = useMemo(() => sortByName(dataList?.cleanRooms),
    [dataList?.cleanRooms],
  );

  useEffect(() => {
    if (orgId) {
      mutateList();
    }
  }, [orgId, mutateList]);

  const getExpandedState = (rowId) => expandedStates[rowId] || {
    questionsOpen: false, partnersOpen: false, flowsOpen: false,
  };

  const setExpandedState = (rowId, newState) => {
    setExpandedStates(prev => ({
      ...prev,
      [rowId]: { ...prev[rowId], ...newState },
    }));
  };

  // Early return if no orgId (after all hooks are called)
  if (!orgId) {
    return (
      <>
        <PageHeader title='Manage Billing' breadcrumbs={[crumbs.home, crumbs.internalAdmin]} />
        <Card>
          <CardContent>
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
              <HSpinner />
              <Typography variant='body2' sx={{ ml: 2 }}>
                Loading organization data...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title='Manage Billing' breadcrumbs={[crumbs.home, crumbs.internalAdmin]} />
      <Card>
        <CardContent>
          <ListViewTable
            list={!(isListLoading || isListFetching) && sortedCleanRooms}
            skeletonHeight={350}
          >
            <TableContainer>
              <Table aria-label='collapsible table'>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell width='25%'>Clean Room Name</TableCell>
                    <TableCell align='right'>Display ID</TableCell>
                    <TableCell align='center'>Type</TableCell>
                    <TableCell align='right'>Billable</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCleanRooms?.map((row) => (
                    <Row
                      key={row.ID}
                      row={row}
                      open={openRowId === row.ID}
                      handleOnClick={() => {
                        setOpenRowId(openRowId === row.ID ? null : row.ID);
                      }}
                      orgId={orgId}
                      expandedState={getExpandedState(row.ID)}
                      setExpandedState={(newState) => setExpandedState(row.ID, newState)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ListViewTable>
        </CardContent>
      </Card>
    </>
  );
};

export default ManageBilling;
